/**
 * Google's Picker: the file chooser that is the only way `drive.file` sees a
 * file.
 *
 * Loaded from Google on demand (`apis.google.com/js/api.js`, then the `picker`
 * module), shown in the page as Google's own frame (`docs.google.com`) — not a
 * popup window, so a popup blocker does not stop it — and given the short-lived
 * access token the server issued to the person whose Google account is
 * connected. The CSP allows exactly those two Google origins (public/_headers).
 *
 * The result is treated as untrusted input, like anything else that arrives in
 * the page: one Google spreadsheet, with an id of Google's own alphabet, or it
 * is refused. The server then asks Google whether the company's grant can open
 * it, which is the check that decides.
 */
import type { PickerConfig } from './google';

export const PICKER_SCRIPT = 'https://apis.google.com/js/api.js';
export const SPREADSHEET_MIME = 'application/vnd.google-apps.spreadsheet';
const SPREADSHEET_ID = /^[A-Za-z0-9_-]{20,120}$/;

export type PickerOutcome =
    | { kind: 'picked'; spreadsheetId: string }
    | { kind: 'cancelled' }
    | { kind: 'refused'; reason: string };

/** The part of `google.picker` this file uses. */
export interface PickerNamespace {
    PickerBuilder: new () => PickerBuilderLike;
    DocsView: new (viewId?: unknown) => DocsViewLike;
    ViewId: { SPREADSHEETS: unknown };
    Feature?: { NAV_HIDDEN?: unknown };
    Action?: { PICKED?: string; CANCEL?: string };
    Response?: { ACTION?: string; DOCUMENTS?: string };
    Document?: { ID?: string; MIME_TYPE?: string };
}
interface DocsViewLike { setMimeTypes(types: string): DocsViewLike }
interface PickerLike { setVisible(visible: boolean): void; dispose?(): void }
interface PickerBuilderLike {
    setAppId(id: string): PickerBuilderLike;
    setOAuthToken(token: string): PickerBuilderLike;
    setDeveloperKey(key: string): PickerBuilderLike;
    setOrigin(origin: string): PickerBuilderLike;
    addView(view: DocsViewLike): PickerBuilderLike;
    enableFeature(feature: unknown): PickerBuilderLike;
    setCallback(callback: (data: Record<string, unknown>) => void): PickerBuilderLike;
    build(): PickerLike;
}

interface GapiWindow {
    gapi?: { load(name: string, options: { callback: () => void; onerror?: () => void; timeout?: number; ontimeout?: () => void }): void };
    google?: { picker?: PickerNamespace };
}

const NOT_LOADED = 'Google’s file chooser could not load. If a content blocker is on, allow apis.google.com and try again.';

let loading: Promise<PickerNamespace> | null = null;

/** Forget a finished load, so each test starts from a page that has not loaded it. */
export function __resetPickerLoaderForTests(): void {
    loading = null;
}

/** Load the Picker once per page. A failed load can be tried again. */
export function loadPicker(win: Window & GapiWindow = window as Window & GapiWindow, timeoutMs = 15_000): Promise<PickerNamespace> {
    if (win.google?.picker) return Promise.resolve(win.google.picker);
    if (loading) return loading;
    loading = new Promise<PickerNamespace>((resolve, reject) => {
        const fail = () => { loading = null; reject(new Error(NOT_LOADED)); };
        const timer = win.setTimeout(fail, timeoutMs);
        const withPicker = () => {
            win.gapi!.load('picker', {
                callback: () => {
                    win.clearTimeout(timer);
                    if (win.google?.picker) resolve(win.google.picker); else fail();
                },
                onerror: () => { win.clearTimeout(timer); fail(); },
            });
        };
        if (win.gapi) { withPicker(); return; }
        const script = win.document.createElement('script');
        script.src = PICKER_SCRIPT;
        script.async = true;
        script.onload = () => (win.gapi ? withPicker() : (win.clearTimeout(timer), fail()));
        script.onerror = () => { win.clearTimeout(timer); fail(); };
        win.document.head.appendChild(script);
    });
    return loading;
}

/**
 * What the Picker said, read without trusting it: exactly one Google
 * spreadsheet with a well-formed id, or a cancel. Anything else is refused.
 */
export function readPickerResult(data: Record<string, unknown>, ns?: Pick<PickerNamespace, 'Action' | 'Response' | 'Document'>): PickerOutcome | null {
    const actionKey = ns?.Response?.ACTION ?? 'action';
    const docsKey = ns?.Response?.DOCUMENTS ?? 'docs';
    const idKey = ns?.Document?.ID ?? 'id';
    const mimeKey = ns?.Document?.MIME_TYPE ?? 'mimeType';
    const picked = ns?.Action?.PICKED ?? 'picked';
    const cancel = ns?.Action?.CANCEL ?? 'cancel';

    const action = data?.[actionKey];
    if (action === cancel) return { kind: 'cancelled' };
    if (action !== picked) return null;   // "loaded" and the like: not an answer yet

    const docs = data[docsKey];
    if (!Array.isArray(docs) || docs.length !== 1) return { kind: 'refused', reason: 'Choose exactly one spreadsheet.' };
    const doc = docs[0] as Record<string, unknown>;
    const id = doc?.[idKey];
    if (typeof id !== 'string' || !SPREADSHEET_ID.test(id)) {
        return { kind: 'refused', reason: 'Google sent back something that is not a spreadsheet we can read.' };
    }
    if (doc[mimeKey] !== SPREADSHEET_MIME) return { kind: 'refused', reason: 'That is not a Google Sheets spreadsheet. Choose a spreadsheet.' };
    return { kind: 'picked', spreadsheetId: id };
}

/** Open the Picker and wait for one answer. */
export function openPicker(ns: PickerNamespace, config: PickerConfig, origin: string): Promise<PickerOutcome> {
    return new Promise(resolve => {
        let settled = false;
        const view = new ns.DocsView(ns.ViewId.SPREADSHEETS).setMimeTypes(SPREADSHEET_MIME);
        let builder = new ns.PickerBuilder()
            // Without the app id a file chosen here is not granted to this app
            // under drive.file, and every read of it fails.
            .setAppId(config.appId)
            .setOAuthToken(config.accessToken)
            .setDeveloperKey(config.developerKey)
            .setOrigin(origin)
            .addView(view);
        if (ns.Feature?.NAV_HIDDEN !== undefined) builder = builder.enableFeature(ns.Feature.NAV_HIDDEN);
        const picker = builder.setCallback(data => {
            const outcome = readPickerResult(data, ns);
            if (!outcome || settled) return;
            settled = true;
            picker.dispose?.();
            resolve(outcome);
        }).build();
        picker.setVisible(true);
    });
}
